import { useState, useRef, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { Question } from '../types';
import OptionButton from './OptionButton';
import TopicBadge from './TopicBadge';
import ReportModal from './ReportModal';
import ConfirmModal from './ConfirmModal';
import MarkdownContent from './MarkdownContent';
import DropdownSlots from './question-renderers/DropdownSlots';
import DragDropSlots from './question-renderers/DragDropSlots';
import YesNoStatements from './question-renderers/YesNoStatements';
import { isMultiAnswer, parseCorrectAnswer, isYesNoGrid, groupYesNoRows, isSlotBased } from '../utils/questionHelpers';
import { resolveMediaUrl } from '../utils/mediaUrl';

interface QuestionCardProps {
  question: Question;
  showAnswer: boolean;
  selectedAnswer: string | string[] | null;
  onSelectOption: (letter: string) => void;
  onReveal?: () => void;
  questionNumber?: number;
  totalQuestions?: number;
  slotSelections?: Record<number, string>;
  onSlotSelect?: (slotId: number, value: string) => void;
  hintRevealed?: boolean;
  onHint?: () => void;
  /** Whether this question is flagged "for review" by the user. */
  isFlagged?: boolean;
  /** Toggle the review flag. When omitted, the flag button is hidden. */
  onToggleFlag?: () => void;
}

function getOptionState(
  letter: string,
  selected: string | string[] | null,
  correctAnswer: string,
  showAnswer: boolean,
  hintRevealed?: boolean,
): 'default' | 'selected' | 'correct' | 'incorrect' | 'disabled' | 'hinted' {
  const isSelected = Array.isArray(selected) ? selected.includes(letter) : letter === selected;

  if (!showAnswer) {
    // Show hint highlight on the correct option(s) when hintRevealed
    if (hintRevealed) {
      const correctLetters = parseCorrectAnswer(correctAnswer);
      if (correctLetters.includes(letter)) return 'hinted';
    }
    return isSelected ? 'selected' : 'default';
  }

  const correctLetters = parseCorrectAnswer(correctAnswer);
  if (correctLetters.includes(letter)) return 'correct';
  if (isSelected && !correctLetters.includes(letter)) return 'incorrect';
  return 'disabled';
}

export default function QuestionCard({
  question,
  showAnswer,
  selectedAnswer,
  onSelectOption,
  onReveal,
  questionNumber,
  totalQuestions,
  slotSelections,
  onSlotSelect,
  hintRevealed = false,
  onHint,
  isFlagged = false,
  onToggleFlag,
}: QuestionCardProps) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [hintConfirmOpen, setHintConfirmOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const articleRef = useRef<HTMLElement>(null);
  const isMultiple = isMultiAnswer(question.question_type);
  const hasOptions = question.options.length > 0;
  const yesNoMode = hasOptions && isYesNoGrid(question.options);
  const yesNoRows = yesNoMode ? groupYesNoRows(question.options) : [];

  // Exhibit images. Some stems carry an inline "*(see image)*" marker where the
  // exhibit belongs; render the image(s) at that position instead of dumping
  // them below the whole stem (and instead of showing the literal marker text).
  const imageUrls = question.images ?? [];
  const stemSegments = question.question_text.split(/[ \t]*\n*[ \t]*\*\(see image\)\*[ \t]*\n*[ \t]*/);
  const markerCount = stemSegments.length - 1;
  const showInlineImages = markerCount > 0 && imageUrls.length > 0;
  // Map images to marker slots: 1:1 when counts match, otherwise all at the first marker.
  const imagesForSlot = (slot: number): string[] =>
    markerCount === imageUrls.length ? [imageUrls[slot]] : slot === 0 ? imageUrls : [];
  const stemClass = 'text-base font-medium leading-relaxed text-gray-900 dark:text-white';
  const renderImg = (src: string) => (
    <img
      key={src}
      src={resolveMediaUrl(src) ?? src}
      alt={t('study.questionCard.imageAlt', {
        number: questionNumber ?? question.id,
        topic: question.topic ?? t('study.questionCard.imageAltFallback'),
        difficulty: question.difficulty,
      })}
      className="max-w-full rounded-lg border border-gray-200 dark:border-gray-700"
      loading="lazy"
    />
  );

  // Determine if this is a slot-based question
  const isSlot = isSlotBased(question);
  const hasSlotSelection = isSlot && slotSelections
    ? Object.values(slotSelections).some((v) => v !== '')
    : false;
  const hasSelection = isSlot
    ? hasSlotSelection
    : (Array.isArray(selectedAnswer) ? selectedAnswer.length > 0 : selectedAnswer !== null);

  // Focus the question card when the question changes for keyboard navigation
  useEffect(() => {
    if (articleRef.current) {
      articleRef.current.focus({ preventScroll: true });
    }
  }, [question.id]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  return (
    <article ref={articleRef} className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_2px_4px_rgba(15,23,42,0.05),0_6px_16px_-6px_rgba(15,23,42,0.12)] space-y-5 dark:border-white/10 dark:bg-card-dark sm:p-8" tabIndex={-1} aria-label={questionNumber != null ? t('study.questionCard.questionOf', { n: questionNumber, total: totalQuestions }) : t('study.questionCard.question')}>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        {questionNumber != null && totalQuestions != null && (
          <span className="rounded-full bg-blue-600 px-3 py-1 text-[12px] font-extrabold text-white font-mono">
            {t('study.questionCard.questionNumberShort', { n: questionNumber, total: totalQuestions })}
          </span>
        )}
        {question.topic && <TopicBadge topic={question.topic} />}
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10.5px] font-extrabold uppercase tracking-wider text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
          {question.difficulty}
        </span>

        {/* ── Flag for review ──────────────────────────────────────────────── */}
        {onToggleFlag && (
          <button
            type="button"
            onClick={onToggleFlag}
            aria-pressed={isFlagged}
            aria-label={isFlagged ? t('study.questionCard.unflag') : t('study.questionCard.flag')}
            title={isFlagged ? t('study.questionCard.unflag') : t('study.questionCard.flag')}
            className={`ml-auto inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors ${
              isFlagged
                ? 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-300'
                : 'border-slate-200 bg-white text-slate-500 hover:border-amber-300 hover:text-amber-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-400 dark:hover:text-amber-300'
            }`}
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill={isFlagged ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1Z" />
            </svg>
            {isFlagged ? t('study.questionCard.flagged') : t('study.questionCard.flagReview')}
          </button>
        )}

        {/* ── Metadata menu ──────────────────────────────────────────────────── */}
        <div className={`relative ${onToggleFlag ? '' : 'ml-auto'}`} ref={menuRef}>
          <button
            type="button"
            aria-label={t('study.questionCard.metadataButton')}
            onClick={() => setMenuOpen((o) => !o)}
            className="p-1 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-600 hover:bg-gray-100
                       dark:hover:text-gray-300 dark:hover:bg-gray-700 transition-colors"
          >
            &#x22EE;
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-7 z-50 w-64 rounded-xl border border-slate-200 bg-white
                            p-3 text-xs space-y-1.5 shadow-[0_10px_40px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-card-dark">
              <p className="font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('study.questionCard.questionInfo')}</p>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('study.questionCard.dbId')}</span>
                <span className="font-mono">{question.id}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-gray-500 shrink-0">{t('study.questionCard.sourceId')}</span>
                <span className="font-mono truncate text-right">{question.source_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('study.questionCard.topic')}</span>
                <span>{question.topic ?? '\u2014'}</span>
              </div>
              {question.subtopic && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 shrink-0">{t('study.questionCard.subtopic')}</span>
                  <span className="text-right">{question.subtopic}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">{t('study.questionCard.type')}</span>
                <span>{question.question_type}</span>
              </div>
              {question.created_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('study.questionCard.added')}</span>
                  <span>{new Date(question.created_at).toLocaleDateString()}</span>
                </div>
              )}
              {question.ms_docs_url && (
                <a
                  href={question.ms_docs_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-2 text-blue-600 dark:text-blue-400 hover:underline truncate"
                >
                  {t('study.questionCard.msLearn')} &#x2197;
                </a>
              )}
              <hr className="border-gray-200 dark:border-gray-600 my-2" />
              <button
                type="button"
                onClick={() => { setMenuOpen(false); setReportOpen(true); }}
                className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5" />
                </svg>
                {t('study.questionCard.reportQuestion')}
              </button>
            </div>
          )}
        </div>
      </div>

      {reportOpen && <ReportModal questionId={question.id} onClose={() => setReportOpen(false)} />}

      {/* ── Question text (with exhibits inlined at "*(see image)*") ──── */}
      {showInlineImages ? (
        <div className="space-y-4">
          {stemSegments.map((seg, i) => (
            <Fragment key={i}>
              {seg.trim() && <MarkdownContent content={seg} className={stemClass} />}
              {i < markerCount && imagesForSlot(i).length > 0 && (
                <div className="flex flex-wrap gap-3">{imagesForSlot(i).map(renderImg)}</div>
              )}
            </Fragment>
          ))}
        </div>
      ) : (
        <MarkdownContent content={question.question_text} className={stemClass} />
      )}
      {isMultiple && !yesNoMode && !isSlot && (
        <p className="text-xs font-medium text-blue-600 dark:text-blue-400">
          {t('study.questionCard.selectAllThatApply')}
        </p>
      )}

      {/* ── Images (no inline marker: show exhibits after the stem) ───── */}
      {!showInlineImages && imageUrls.length > 0 && (
        <div className="flex flex-wrap gap-3">{imageUrls.map(renderImg)}</div>
      )}

      {/* ── Slot-based renderers (dropdown, sortable-list, yes-no-statements) ─ */}
      {isSlot && question.answer_slots && (
        question.render_as === 'dropdown' ? (
          <DropdownSlots
            slots={question.answer_slots}
            selections={slotSelections ?? {}}
            onSelect={onSlotSelect ?? (() => {})}
            revealed={showAnswer}
            hintRevealed={hintRevealed}
          />
        ) : question.render_as === 'sortable-list' ? (
          <DragDropSlots
            slots={question.answer_slots}
            selections={slotSelections ?? {}}
            onSelect={onSlotSelect ?? (() => {})}
            revealed={showAnswer}
            hintRevealed={hintRevealed}
          />
        ) : question.render_as === 'yes-no-statements' ? (
          <YesNoStatements
            slots={question.answer_slots}
            selections={slotSelections ?? {}}
            onSelect={onSlotSelect ?? (() => {})}
            revealed={showAnswer}
            hintRevealed={hintRevealed}
          />
        ) : null
      )}

      {/* ── Yes/No Grid (legacy hotspot with row-based Yes/No options) ── */}
      {!isSlot && yesNoMode && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">
            {t('study.questionCard.yesNoPrompt')}
          </p>
          {yesNoRows.map(({ row }) => {
            const selectedArr = Array.isArray(selectedAnswer) ? selectedAnswer : [];
            const yesLetter = `${row}-Yes`;
            const noLetter = `${row}-No`;
            const yesState = getOptionState(yesLetter, selectedAnswer, question.correct_answer, showAnswer, hintRevealed);
            const noState = getOptionState(noLetter, selectedAnswer, question.correct_answer, showAnswer, hintRevealed);
            const yesSelected = selectedArr.includes(yesLetter);
            const noSelected = selectedArr.includes(noLetter);

            return (
              <div key={row} className="flex items-center gap-2">
                <span className="w-16 shrink-0 text-sm font-semibold text-gray-500 dark:text-gray-400">
                  {t('study.questionCard.row', { n: row })}
                </span>
                <button
                  type="button"
                  disabled={showAnswer}
                  onClick={() => {
                    if (noSelected) onSelectOption(noLetter);
                    if (!yesSelected) onSelectOption(yesLetter);
                  }}
                  className={`flex-1 rounded-xl border-2 px-4 py-2.5 text-sm font-semibold text-center transition-all ${
                    showAnswer
                      ? yesState === 'correct'
                        ? 'border-green-500 bg-green-50 text-green-800 dark:border-green-500 dark:bg-green-900/20 dark:text-green-300'
                        : yesState === 'incorrect'
                          ? 'border-red-500 bg-red-50 text-red-800 dark:border-red-500 dark:bg-red-900/20 dark:text-red-300'
                          : 'border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-600'
                      : yesSelected
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/30 text-primary dark:border-primary dark:bg-primary/20'
                        : 'border-gray-200 bg-white hover:border-primary hover:bg-primary/5 text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-primary'
                  }`}
                >
                  {t('study.questionCard.yes')} {showAnswer && yesState === 'correct' && '\u2713'}{showAnswer && yesState === 'incorrect' && '\u2717'}
                </button>
                <button
                  type="button"
                  disabled={showAnswer}
                  onClick={() => {
                    if (yesSelected) onSelectOption(yesLetter);
                    if (!noSelected) onSelectOption(noLetter);
                  }}
                  className={`flex-1 rounded-xl border-2 px-4 py-2.5 text-sm font-semibold text-center transition-all ${
                    showAnswer
                      ? noState === 'correct'
                        ? 'border-green-500 bg-green-50 text-green-800 dark:border-green-500 dark:bg-green-900/20 dark:text-green-300'
                        : noState === 'incorrect'
                          ? 'border-red-500 bg-red-50 text-red-800 dark:border-red-500 dark:bg-red-900/20 dark:text-red-300'
                          : 'border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-600'
                      : noSelected
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/30 text-primary dark:border-primary dark:bg-primary/20'
                        : 'border-gray-200 bg-white hover:border-primary hover:bg-primary/5 text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-primary'
                  }`}
                >
                  {t('study.questionCard.no')} {showAnswer && noState === 'correct' && '\u2713'}{showAnswer && noState === 'incorrect' && '\u2717'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Standard Options (A/B/C/D style) ──────────────────────────── */}
      {!isSlot && hasOptions && !yesNoMode && (
        <div
          className="space-y-2"
          role={isMultiple ? undefined : 'radiogroup'}
          aria-label={isMultiple ? undefined : t('study.questionCard.answerOptionsLabel')}
        >
          {question.options.map((opt) => (
            <OptionButton
              key={opt.id}
              letter={opt.letter}
              text={opt.option_text}
              state={getOptionState(opt.letter, selectedAnswer, question.correct_answer, showAnswer, hintRevealed)}
              onClick={() => onSelectOption(opt.letter)}
              role={isMultiple ? undefined : 'radio'}
            />
          ))}
        </div>
      )}

      {/* ── Hint + Reveal buttons (study mode) ───────────────────────── */}
      {onReveal && !showAnswer && (
        <div className="flex gap-3 flex-wrap">
          {onHint && !hintRevealed && (
            <button
              type="button"
              onClick={() => setHintConfirmOpen(true)}
              className="btn-secondary text-sm inline-flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('study.questionCard.hint')}
            </button>
          )}
          {hasSelection && (
            <button
              type="button"
              onClick={onReveal}
              className="btn-primary w-full sm:w-auto"
            >
              {t('study.questionCard.checkAnswer')}
            </button>
          )}
        </div>
      )}

      {/* ── Hint confirmation modal ───────────────────────────────────── */}
      {hintConfirmOpen && onHint && (
        <ConfirmModal
          title={t('study.questionCard.revealAnswerTitle')}
          message={t('study.questionCard.revealAnswerMessage')}
          confirmLabel={t('study.questionCard.reveal')}
          cancelLabel={t('study.questionCard.cancel')}
          variant="warning"
          onConfirm={() => {
            setHintConfirmOpen(false);
            onHint();
          }}
          onCancel={() => setHintConfirmOpen(false)}
        />
      )}

      {/* ── Explanation / correct answer (after reveal) ───────────────── */}
      {showAnswer && (
        <div className="rounded-2xl border border-green-600/50 bg-white p-6 space-y-3 ring-[3px] ring-green-600/10 shadow-[0_2px_4px_rgba(15,23,42,0.05),0_6px_16px_-6px_rgba(15,23,42,0.12)] dark:bg-card-dark sm:p-8" aria-live="polite" role="status">
          {isSlot && question.answer_slots ? (
            <div className="space-y-1">
              {question.answer_slots.map((slot) => (
                <p key={slot.slot_id} className="text-sm font-semibold text-green-800 dark:text-green-300">
                  {slot.slot_text}: {slot.correct_answer}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-sm font-semibold text-green-800 dark:text-green-300">
              {t('study.questionCard.correctAnswerLabel', { answer: parseCorrectAnswer(question.correct_answer).join(', ') })}
            </p>
          )}
          {question.explanation && (
            <MarkdownContent
              content={question.explanation}
              className="text-sm leading-relaxed text-gray-700 dark:text-gray-300"
            />
          )}
          {question.ms_docs_url && (
            <a
              href={question.ms_docs_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              {t('study.questionCard.microsoftLearn')}
              <span aria-hidden="true">&#x2197;</span>
            </a>
          )}
          {question.community_votes && Object.keys(question.community_votes).length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('study.questionCard.communityVotes')}</span>
              {Object.entries(question.community_votes).map(([key, count]) => (
                <span
                  key={key}
                  className="rounded bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                >
                  {key}: {count}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
}
